<section
  class="capture-builder"
  [class.inspector-mode]="inspectorMode"
  aria-label="Capture form builder"
>
  <p *ngIf="unsupported" role="alert">
    This form cannot be edited here. Its saved content is preserved.
  </p>
  <p *ngIf="hasUnknownSettings" role="status">
    Some saved settings are unavailable in this editor. They are preserved.
  </p>
  <p *ngIf="error" role="alert">{{ error }}</p>
  <div *ngIf="recoveryFields.length" class="dependency-recovery" aria-label="Review dependent visibility">
    <button *ngFor="let dependent of recoveryFields; trackBy: trackField" uiButton type="button" [text]="true" [wrap]="true"
      [attr.aria-label]="'Review visibility for ' + fieldLabel(dependent)" (click)="reviewDependency(dependent.id)">Review {{ fieldLabel(dependent) }}</button>
  </div>
  <button
    uiButton
    severity="primary"
    [outlined]="false"
    *ngIf="!form"
    type="button"
    [disabled]="disabled"
    (click)="initialize()"
  >
    Create blank form
  </button>
  <ng-container *ngIf="form && !unsupported">
    <ng-container *ngIf="formSettingsVisible">
      <fieldset class="form-heading" [disabled]="disabled">
        <label
          >Form title<input
            uiControl
            aria-label="Form title"
            maxlength="120"
            [ngModel]="form.title"
            (ngModelChange)="setTitle($event)"
        /></label>
        <span>{{ fields.length }} / 50 fields</span>
      </fieldset>
      <details *ngIf="copies.length" class="copy-form">
        <summary>Reuse a form from another step</summary>
        <label
          >Existing form<select
            uiControl
            aria-label="Copy form from another step"
            [(ngModel)]="selectedCopy"
            [disabled]="disabled"
          >
            <option value="">Choose a form</option>
            <option
              *ngFor="let copy of copies; trackBy: trackCopy"
              [value]="copy.key"
            >
              {{ copy.label }}
            </option>
          </select></label
        >
        <p class="hint">
          Replaces this form with an independent copy. Later edits on either
          step do not change the other.
        </p>
        <button
          uiButton
          type="button"
          [disabled]="disabled || !selectedCopy"
          (click)="copy()"
        >
          Replace with independent copy
        </button>
      </details>
      <p *ngIf="formIssue" role="alert">{{ formIssue }}</p>
      <div
        *ngIf="!inspectorMode && showAddField"
        class="field-palette"
        aria-label="Add form field"
      >
        <button
          uiButton
          *ngFor="let type of types"
          type="button"
          [disabled]="disabled || fields.length >= 50"
          [attr.aria-label]="'Add ' + type.label + ' field'"
          (click)="add(type.code)"
        >
          + {{ type.label }}
        </button>
      </div>
      <label *ngIf="inspectorMode && showAddField"
        >Add field<select
          uiControl
          aria-label="Add form field"
          #fieldType
          [disabled]="disabled || fields.length >= 50"
          (change)="add($any(fieldType.value)); fieldType.value = ''"
        >
          <option value="" disabled selected>Choose a field type</option>
          <option *ngFor="let type of types" [value]="type.code">
            {{ type.label }}
          </option>
        </select></label
      >
      <div
        *ngIf="invalidFields.length"
        class="issue-list"
        aria-label="Fields needing attention"
      >
        <strong
          >{{ invalidFields.length }}
          {{ invalidFields.length === 1 ? "field needs" : "fields need" }}
          attention</strong
        ><button
          uiButton
          [wrap]="true"
          [text]="true"
          [outlined]="false"
          type="button"
          *ngFor="let field of invalidFields; trackBy: trackField"
          (click)="focusFieldIssue(field.id)"
        >
          {{ fieldLabel(field) }}: {{ issue(field) }}
        </button>
      </div>
      <p *ngIf="!fields.length" class="empty">
        Add a field to start building the form.
      </p>
    </ng-container>
    <p
      *ngIf="inspectorMode && selectedFieldId !== null && !visibleFields.length"
      role="status"
    >
      Select a field from the page.
    </p>
    <article
      *ngFor="let field of visibleFields; trackBy: trackField"
      class="capture-field"
      [attr.data-capture-field]="field.id"
    >
      <header [class.inspector-header]="inspectorMode">
        <fieldset
          *ngIf="!inspectorMode"
          class="field-quick-edit"
          [disabled]="disabled || !knownType(field)"
        >
          <label
            >Field label<input
              uiControl
              data-field-setting="label"
              [attr.aria-label]="fieldLabel(field) + ' label'"
              maxlength="120"
              [ngModel]="field.label"
              (ngModelChange)="update(field.id, { label: $event })"
          /></label>
          <label class="check"
            ><input
              uiControl
              type="checkbox"
              [attr.aria-label]="fieldLabel(field) + ' required'"
              [ngModel]="field.required"
              (ngModelChange)="update(field.id, { required: $event })"
            />Required answer</label
          >
        </fieldset>
        <button
          uiButton
          [text]="true"
          [outlined]="false"
          *ngIf="!inspectorMode"
          type="button"
          class="field-settings-toggle"
          [attr.aria-label]="'Settings for ' + fieldLabel(field)"
          [attr.aria-expanded]="selectedField === field.id"
          (click)="selectField(selectedField === field.id ? null : field.id)"
        >
          {{ typeLabel(field) }} · Settings
          {{ selectedField === field.id ? "▴" : "▾" }}
        </button>
        <strong *ngIf="inspectorMode" class="inspector-field-label">{{
          fieldLabel(field)
        }}</strong>
        <div *ngIf="inspectorMode; else inlineFieldActions" class="field-actions">
          <button uiButton type="button" [size]="undefined" [text]="true" [outlined]="false"
            [attr.aria-label]="'Field actions for ' + fieldLabel(field)" title="Field actions"
            [disabled]="disabled" #fieldMenuButton #fieldMenuTrigger="cdkMenuTriggerFor" [cdkMenuTriggerFor]="fieldActionsMenu" (cdkMenuOpened)="captureFieldActions(field)">
            <i class="pi pi-ellipsis-h" aria-hidden="true"></i>
          </button>
          <ng-template #fieldActionsMenu>
            <div cdkMenu class="capture-field-menu" aria-label="Field actions"
              (keydown.escape)="$event.preventDefault(); $event.stopPropagation(); fieldMenuTrigger.close(); fieldMenuButton.focus()">
              <button uiButton cdkMenuItem type="button" [disabled]="disabled || fieldPosition(field) === 0"
                [cdkMenuItemDisabled]="disabled || fieldPosition(field) === 0"
                [attr.aria-label]="'Move ' + fieldLabel(field) + ' earlier'"
                (cdkMenuItemTriggered)="runFieldAction('earlier', field)">Move earlier</button>
              <button uiButton cdkMenuItem type="button" [disabled]="disabled || fieldPosition(field) === fields.length - 1"
                [cdkMenuItemDisabled]="disabled || fieldPosition(field) === fields.length - 1"
                [attr.aria-label]="'Move ' + fieldLabel(field) + ' later'"
                (cdkMenuItemTriggered)="runFieldAction('later', field)">Move later</button>
              <button uiButton cdkMenuItem type="button" severity="danger" [text]="true" [outlined]="false"
                [disabled]="disabled" [cdkMenuItemDisabled]="disabled"
                [attr.aria-label]="'Remove ' + fieldLabel(field) + ' field'"
                (cdkMenuItemTriggered)="runFieldAction('remove', field)">Remove field</button>
            </div>
          </ng-template>
        </div>
        <ng-template #inlineFieldActions>
        <div class="field-actions">
          <button
            *ngIf="!inspectorMode || fields.length > 1"
            uiButton
            type="button"
            [disabled]="disabled || fieldPosition(field) === 0"
            [attr.aria-label]="'Move ' + fieldLabel(field) + ' earlier'"
            [title]="'Move ' + fieldLabel(field) + ' earlier'"
            (click)="move(field, -1)"
          >
            ↑
          </button>
          <button
            *ngIf="!inspectorMode || fields.length > 1"
            uiButton
            type="button"
            [disabled]="disabled || fieldPosition(field) === fields.length - 1"
            [attr.aria-label]="'Move ' + fieldLabel(field) + ' later'"
            [title]="'Move ' + fieldLabel(field) + ' later'"
            (click)="move(field, 1)"
          >
            ↓
          </button>
          <button
            uiButton
            severity="danger"
            [text]="true"
            [outlined]="false"
            type="button"
            [disabled]="disabled"
            [attr.aria-label]="'Remove ' + fieldLabel(field) + ' field'"
            (click)="remove(field)"
          >
            Remove
          </button>
        </div>
        </ng-template>
      </header>
      <p *ngIf="issue(field)" class="field-error" role="alert">
        {{ issue(field) }}
      </p>
      <div
        *ngIf="inspectorMode || selectedField === field.id"
        class="field-content"
        role="group"
        tabindex="-1"
        [attr.aria-label]="'Settings for ' + fieldLabel(field)"
      >
        <fieldset [disabled]="disabled || !knownType(field)">
          <div class="field-controls">
            <label *ngIf="inspectorMode"
              >Field label<input
                uiControl
                data-field-setting="label"
                [attr.aria-label]="fieldLabel(field) + ' label'"
                maxlength="120"
                [ngModel]="field.label"
                (ngModelChange)="update(field.id, { label: $event })"
            /></label>
            <label
              >Input type<select
                uiControl
                [attr.aria-label]="fieldLabel(field) + ' type'"
                [ngModel]="field.type"
                #fieldTypeModel="ngModel"
                [attr.data-capture-type-origin]="typeOrigin(field)"
                (change)="changeTypeFromControl(field, $event, fieldTypeModel)"
              >
                <option *ngFor="let type of types" [value]="type.code">
                  {{ type.label }}
                </option>
                <option *ngIf="!knownType(field)" [value]="field.type">
                  Unavailable type (saved)
                </option>
              </select></label
            >
            <label *ngIf="inspectorMode" class="check"
              ><input
                uiControl
                type="checkbox"
                [attr.aria-label]="fieldLabel(field) + ' required'"
                [ngModel]="field.required"
                (ngModelChange)="update(field.id, { required: $event })"
              />Required answer</label
            >
            <ng-container *ngIf="layoutEnabled || inspectorMode">
              <h5 class="layout-heading">Layout</h5>
              <label
                >Width<select
                  uiControl
                  [attr.aria-label]="fieldLabel(field) + ' width'"
                  [disabled]="!canEditLayout"
                  [ngModel]="field.layout?.width ?? 'fill'" #widthModel="ngModel"
                  (ngModelChange)="setLayout(field, { width: $event }, widthModel)"
                >
                  <option
                    *ngFor="let width of widthChoices"
                    [value]="width.value"
                    [title]="layoutChoiceReason(field, { width: width.value })"
                    [disabled]="
                      !!layoutChoiceReason(field, { width: width.value })
                    "
                  >
                    {{ width.label }}
                  </option></select
                ><small
                  class="hint"
                  *ngIf="
                    canEditLayout &&
                    layoutChoiceReason(field, { width: 'fill' })
                  "
                  >{{ layoutChoiceReason(field, { width: "fill" }) }}</small
                ></label
              >
              <label
                >Row placement<select
                  uiControl
                  [attr.aria-label]="fieldLabel(field) + ' row placement'"
                  [disabled]="!canEditLayout"
                  [ngModel]="field.layout?.row ?? 'new'" #rowModel="ngModel"
                  (ngModelChange)="setLayout(field, { row: $event }, rowModel)"
                >
                  <option value="new">Start new row</option>
                  <option
                    value="previous"
                    [disabled]="
                      !!layoutChoiceReason(field, { row: 'previous' })
                    "
                  >
                    Same row as previous field
                  </option></select
                ><small
                  class="hint"
                  *ngIf="layoutChoiceReason(field, { row: 'previous' })"
                  >{{ layoutChoiceReason(field, { row: "previous" }) }}</small
                ></label
              >
              <p class="hint layout-hint">
                Preferred widths wrap to fit the form. Narrow forms stack fields
                in reading order.
              </p>
              <div class="row-actions" *ngIf="rowCount > 1">
                <button
                  uiButton
                  type="button"
                  [disabled]="!canEditLayout || rowPosition(field) <= 0"
                  [attr.aria-label]="
                    'Move row containing ' + fieldLabel(field) + ' earlier'
                  "
                  (click)="moveRow(field, -1)"
                >
                  Move row earlier
                </button>
                <button
                  uiButton
                  type="button"
                  [disabled]="
                    !canEditLayout || rowPosition(field) >= rowCount - 1
                  "
                  [attr.aria-label]="
                    'Move row containing ' + fieldLabel(field) + ' later'
                  "
                  (click)="moveRow(field, 1)"
                >
                  Move row later
                </button>
              </div>
            </ng-container>
            <label *ngIf="field.type === 'text' || field.type === 'textarea'"
              >Maximum characters<input
                uiControl
                type="number"
                min="1"
                max="100000"
                step="1"
                placeholder="4000"
                [attr.aria-label]="fieldLabel(field) + ' maximum characters'"
                [ngModel]="field.maxLength ?? null"
                (ngModelChange)="update(field.id, { maxLength: $event })"
            /></label>
            <ng-container
              *ngIf="field.type === 'number' || field.type === 'integer'"
            >
              <label
                >Minimum<input
                  uiControl
                  type="number"
                  min="-1000000000000000"
                  max="1000000000000000"
                  step="any"
                  [attr.aria-label]="fieldLabel(field) + ' minimum'"
                  [ngModel]="field.minimum ?? null"
                  (ngModelChange)="update(field.id, { minimum: $event })"
              /></label>
              <label
                >Maximum<input
                  uiControl
                  type="number"
                  min="-1000000000000000"
                  max="1000000000000000"
                  step="any"
                  [attr.aria-label]="fieldLabel(field) + ' maximum'"
                  [ngModel]="field.maximum ?? null"
                  (ngModelChange)="update(field.id, { maximum: $event })"
              /></label>
            </ng-container>
          </div>
          <div *ngIf="lookupSourceAvailable && field.type === 'select'" class="lookup-source-settings">
            <label>Choices source<select uiControl [attr.aria-label]="fieldLabel(field) + ' choices source'"
              [disabled]="disabled" [value]="field.lookup ? 'lookup' : 'inline'" (change)="requestChoiceSource(field, $event)">
              <option value="inline">Choices defined here</option>
              <option value="lookup" [disabled]="!!lookupSourceReason">Lookup administration</option>
            </select></label>
            <p *ngIf="lookupSourceReason" class="hint" role="status">{{ lookupSourceReason }}</p>
            <button *ngIf="field.lookup" uiButton type="button" [disabled]="disabled || !!lookupSourceReason"
              [attr.aria-label]="'Change lookup source for ' + fieldLabel(field)" (click)="requestLookupSource(field)">Change lookup source</button>
            <p *ngIf="field.lookup" class="hint">Choices defined here starts an empty inline list. Lookup choices are not copied.</p>
          </div>
          <p *ngIf="field.lookup" data-capture-lookup>
            Choices come from the saved lookup
            {{ field.lookup.configurationCode }} /
            {{ field.lookup.domainCode }}. The server resolves current choices
            for each visit.<ng-container *ngIf="!lookupSourceAvailable"> Lookup source editing is not available here.</ng-container>
          </p>
          <div
            *ngIf="field.type === 'select' && !field.lookup"
            class="choice-list"
            cdkDropList
            [cdkDropListData]="options(field)"
            [cdkDropListDisabled]="disabled"
            (cdkDropListDropped)="dropOption(field, $event)"
          >
            <div
              *ngFor="
                let option of options(field);
                let at = index;
                trackBy: trackOption
              "
              class="choice-row"
              cdkDrag
              [cdkDragDisabled]="disabled"
            >
              <span
                cdkDragHandle
                class="choice-drag-handle"
                aria-hidden="true"
                title="Drag to reorder choice"
                >⋮⋮</span
              >
              <label
                >Stored value<input
                  uiControl
                  maxlength="120"
                  [attr.aria-label]="
                    fieldLabel(field) + ' choice ' + (at + 1) + ' value'
                  "
                  [value]="option.value"
                  [attr.data-choice-origin]="choiceOrigin(field, at)"
                  data-choice-property="value"
                  (input)="updateOptionFromInput(field, at, 'value', $event)"
              /></label>
              <label
                >Display label<input
                  uiControl
                  maxlength="120"
                  [attr.aria-label]="
                    fieldLabel(field) + ' choice ' + (at + 1) + ' label'
                  "
                  [value]="option.label"
                  [attr.data-choice-origin]="choiceOrigin(field, at)"
                  data-choice-property="label"
                  (input)="updateOptionFromInput(field, at, 'label', $event)"
              /></label>
              <div class="choice-actions">
                <button
                  uiButton
                  type="button"
                  [disabled]="at === 0"
                  [attr.aria-label]="
                    'Move ' +
                    fieldLabel(field) +
                    ' choice ' +
                    (at + 1) +
                    ' earlier'
                  "
                  title="Move choice earlier"
                  (click)="moveOption(field, at, -1)"
                >
                  ↑
                </button>
                <button
                  uiButton
                  type="button"
                  [disabled]="at === options(field).length - 1"
                  [attr.aria-label]="
                    'Move ' +
                    fieldLabel(field) +
                    ' choice ' +
                    (at + 1) +
                    ' later'
                  "
                  title="Move choice later"
                  (click)="moveOption(field, at, 1)"
                >
                  ↓
                </button>
                <button
                  uiButton
                  severity="danger"
                  [text]="true"
                  [outlined]="false"
                  type="button"
                  [attr.aria-label]="
                    fieldLabel(field) + ' remove choice ' + (at + 1)
                  "
                  (click)="removeOption(field, at)"
                >
                  Remove
                </button>
              </div>
            </div>
            <button
              uiButton
              type="button"
              [disabled]="options(field).length >= 100"
              (click)="addOption(field)"
            >
              Add choice
            </button>
          </div>
          <div class="visibility-settings">
            <label
              >Show this field<select
                uiControl
                data-field-setting="visibility"
                [attr.aria-label]="fieldLabel(field) + ' visibility field'"
                [ngModel]="field.visibleWhen?.fieldId || ''"
                (ngModelChange)="setController(field, $event)"
              >
                <option value="">Always</option>
                <option
                  *ngFor="let earlier of controllers(field)"
                  [value]="earlier.id"
                >
                  When {{ earlier.label || "Untitled field" }} matches
                </option>
                <option
                  *ngIf="field.visibleWhen && !controller(field)"
                  [value]="field.visibleWhen.fieldId"
                >
                  Unavailable controlling field (saved)
                </option>
              </select></label
            >
            <ng-container *ngIf="controller(field) as earlier">
              <label *ngIf="earlier.type === 'boolean'"
                >Matching answer<select
                  uiControl
                  data-field-setting="visibility-answer"
                  [attr.aria-label]="fieldLabel(field) + ' visibility answer'"
                  [ngModel]="field.visibleWhen?.equals"
                  (ngModelChange)="setEquals(field, $event)"
                >
                  <option [ngValue]="true">Yes</option>
                  <option [ngValue]="false">No</option>
                </select></label
              >
              <label *ngIf="earlier.type === 'select' && !earlier.lookup"
                >Matching choice<select
                  uiControl
                  data-field-setting="visibility-answer"
                  [attr.aria-label]="fieldLabel(field) + ' visibility answer'"
                  [ngModel]="field.visibleWhen?.equals"
                  (ngModelChange)="setEquals(field, $event)"
                >
                  <option value="">Choose an answer</option>
                  <option *ngIf="unavailableMatchingChoice(field)" [value]="field.visibleWhen?.equals">Unavailable saved choice: {{ field.visibleWhen?.equals }}</option>
                  <option
                    *ngFor="let option of options(earlier)"
                    [value]="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select><small *ngIf="field.visibleWhen?.equals !== ''" class="matching-stored-value">Stored value: <code>{{ field.visibleWhen?.equals }}</code></small></label
              >
              <label *ngIf="earlier.type === 'select' && earlier.lookup"
                >Matching stored code<input
                  uiControl
                  data-field-setting="visibility-answer"
                  [attr.aria-label]="
                    fieldLabel(field) + ' visibility stored code'
                  "
                  [ngModel]="field.visibleWhen?.equals"
                  (ngModelChange)="setEquals(field, $event)"
                  maxlength="120"
              /></label>
              <label
                *ngIf="earlier.type === 'number' || earlier.type === 'integer'"
                >Matching number<input
                  uiControl
                  type="number"
                  [attr.step]="earlier.type === 'integer' ? '1' : 'any'"
                  data-field-setting="visibility-answer"
                  [attr.aria-label]="fieldLabel(field) + ' visibility answer'"
                  [ngModel]="field.visibleWhen?.equals"
                  (ngModelChange)="setEquals(field, $event)"
              /></label>
              <label
                *ngIf="
                  earlier.type === 'date' ||
                  earlier.type === 'text' ||
                  earlier.type === 'textarea'
                "
                >Matching answer<input
                  uiControl
                  [type]="earlier.type === 'date' ? 'date' : 'text'"
                  data-field-setting="visibility-answer"
                  [attr.aria-label]="fieldLabel(field) + ' visibility answer'"
                  [ngModel]="field.visibleWhen?.equals"
                  (ngModelChange)="setEquals(field, $event)"
              /></label>
              <p class="hint">
                Only an earlier, visible answer can reveal this field. Hidden
                fields do not require or retain submitted answers.
              </p>
            </ng-container>
          </div>
        </fieldset>
        <details class="identity">
          <summary>Field identifier</summary>
          <code>{{ field.id }}</code>
        </details>
      </div>
    </article>
  </ng-container>
</section>
